import axiosInstance from "./axiosInstance";
import { API_ENDPOINTS } from "./apiConstants";
import { migrateSysconfigFields } from "./sysconfigMigration";

const STORAGE_MANAGER_HEADERS = {
  "x-output-storage-manager-type":
    process.env.REACT_APP_UPLOAD_FILES_STORAGE_MANAGER_TYPE || "AzureStorageManager",
};

/**
 * Legacy sysconfig caller -- still used by Microgrid pages and dashboards.
 * Prefer fetchSysconfig / saveSysconfig for new code.
 *
 * @param {Object} params
 * @param {string} params.accountname - The storage account name.
 * @param {string} params.simulationName - The simulation name.
 * @param {string} params.datasetName - The dataset name.
 * @param {string} params.token - The token.
 * @param {string} params.method - The method.
 * @param {Object} params.body - The body.
 * @returns {Promise<Object>} The response from the sysconfig API call.
 */
export const sysConfigApi = async ({
  accountname,
  simulationName,
  datasetName,
  token,
  method = "GET",
  body = null,
}) => {
  const baseUrl = process.env.REACT_APP_BASE_URL || "";
  if (!baseUrl) {
    throw new Error("Missing REACT_APP_BASE_URL. Cannot call sysconfig_json.");
  }
  if (!token) {
    throw new Error("Not authenticated (missing access token).");
  }
  if (!accountname) {
    throw new Error("Missing storage_account (accountname).");
  }
  if (!simulationName) {
    throw new Error("Missing microgrid_name (simulationName).");
  }
  if (!datasetName) {
    throw new Error("Missing dataset_name (datasetName).");
  }

  const params = new URLSearchParams({
    storage_account: accountname ?? "",
    microgrid_name: simulationName ?? "",
    dataset_name: datasetName ?? "",
  });
  const url = `${baseUrl}/storage_manager/sysconfig_json?${params.toString()}`;

  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    "x-input-storage-manager-type":
      process.env.REACT_APP_UPLOAD_FILES_STORAGE_MANAGER_TYPE,
    "x-output-storage-manager-type":
      process.env.REACT_APP_UPLOAD_FILES_STORAGE_MANAGER_TYPE,
    Authorization: `Bearer ${token}`,
    "X-User-ID": sessionStorage.getItem("UserID") || "",
  };

  const response = await fetch(url, {
    method: method,
    headers,
    body,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(
      `sysconfig_json failed: ${response.status} ${response.statusText}\n${errorText}\nURL: ${url}`,
    );
  }

  const parsed = await response.json();

  if (method === "GET") {
    const { sysconfig: migratedResult, migrated: didMigrate, details } =
      migrateSysconfigFields(parsed);
    if (didMigrate) {
      console.log("[sysconfig-migration] Legacy sysconfig detected", {
        storageAccount: accountname,
        microgridName: simulationName,
        datasetName,
        legacyFieldDetected: true,
        bothFieldsPresent: details.some((d) => d.action === "conflict_resolved"),
        removedConsumptionDevices: true,
        details,
      });
      try {
        await saveSysconfig({
          storageAccount: accountname,
          microgridName: simulationName,
          datasetName,
          sysconfig: migratedResult,
        });
        console.log("[sysconfig-migration] Persisted migrated sysconfig successfully", {
          storageAccount: accountname,
          microgridName: simulationName,
          datasetName,
          persistAttempted: true,
          persistSucceeded: true,
        });
      } catch (err) {
        console.error("[sysconfig-migration] Failed to persist migrated sysconfig", {
          storageAccount: accountname,
          microgridName: simulationName,
          datasetName,
          persistAttempted: true,
          persistSucceeded: false,
          error: err.message,
        });
      }
    }
    return migratedResult;
  }

  return parsed;
};

/**
 * Fetch sysconfig JSON via axiosInstance.
 * Canonical transport for all features (devices, microgrids, dashboards).
 */
export async function fetchSysconfig({
  storageAccount,
  microgridName,
  datasetName,
}) {
  const params = new URLSearchParams({
    storage_account: storageAccount,
    microgrid_name: microgridName,
    dataset_name: datasetName,
  });
  const { data } = await axiosInstance.get(
    `/storage_manager/sysconfig_json?${params}`,
    { headers: STORAGE_MANAGER_HEADERS },
  );
  const raw = typeof data === "string" ? JSON.parse(data) : data;

  const { sysconfig: migratedResult, migrated: didMigrate, details } =
    migrateSysconfigFields(raw);
  if (didMigrate) {
    console.log("[sysconfig-migration] Legacy sysconfig detected", {
      storageAccount,
      microgridName,
      datasetName,
      legacyFieldDetected: true,
      bothFieldsPresent: details.some((d) => d.action === "conflict_resolved"),
      removedConsumptionDevices: true,
      details,
    });
    try {
      await saveSysconfig({
        storageAccount,
        microgridName,
        datasetName,
        sysconfig: migratedResult,
      });
      console.log("[sysconfig-migration] Persisted migrated sysconfig successfully", {
        storageAccount,
        microgridName,
        datasetName,
        persistAttempted: true,
        persistSucceeded: true,
      });
    } catch (err) {
      console.error("[sysconfig-migration] Failed to persist migrated sysconfig", {
        storageAccount,
        microgridName,
        datasetName,
        persistAttempted: true,
        persistSucceeded: false,
        error: err.message,
      });
    }
  }
  return migratedResult;
}

/**
 * Save (POST) sysconfig JSON via axiosInstance.
 * Canonical transport for all features (devices, microgrids, dashboards).
 */
export async function saveSysconfig({
  storageAccount,
  microgridName,
  datasetName,
  sysconfig,
}) {
  const params = new URLSearchParams({
    storage_account: storageAccount,
    microgrid_name: microgridName,
    dataset_name: datasetName,
  });
  const { data } = await axiosInstance.post(
    `/storage_manager/sysconfig_json?${params}`,
    sysconfig,
    { headers: STORAGE_MANAGER_HEADERS },
  );
  return typeof data === "string" ? JSON.parse(data) : data;
}

/**
 * Delete a sysconfig dataset via the dedicated delete_datasets endpoint.
 * The sysconfig_json path only supports GET/POST — deletions must go
 * through /storage_manager/delete_datasets with the names in the body.
 */
export async function deleteSysconfigDataset({
  storageAccount,
  microgridName,
  datasetName,
}) {
  const params = new URLSearchParams({
    storage_account: storageAccount,
    microgrid_name: microgridName,
  });
  const { data } = await axiosInstance.delete(
    `${API_ENDPOINTS.DELETE_DATASETS}?${params}`,
    {
      headers: STORAGE_MANAGER_HEADERS,
      data: {
        dataset_type: "sysconfig",
        dataset_names_array: [datasetName],
      },
    },
  );
  return data;
}
